/**
 * ─── BIR Form 2316 Generator & Viewer ──────────────────────
 * Generate annual BIR Certificate of Compensation Payment.
 * Uses TaxService.computeForm2316() with TRAIN Law rates.
 */

import { useEffect, useState } from 'react'
import { Card, CardHeader, Badge, Button } from '@repo/ui-core'
import { employeeRepo, payrollRepo } from '../../lib/db'
import { TaxService, DEFAULT_PAYROLL_CONFIG, EMPLOYMENT_STATUS_LABELS } from '@repo/entity-hris'
import type { Employee } from '@repo/entity-hris'
import { FileText, Download, Printer, Eye, EyeOff } from 'lucide-react'

export function HrisForm2316Page() {
  const [employees, setEmployees] = useState<Employee[]>([])
  const [selectedEmp, setSelectedEmp] = useState<string>('')
  const [formData, setFormData] = useState<any>(null)
  const [formattedText, setFormattedText] = useState('')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')

  useEffect(() => {
    (async () => {
      try {
        const res = await employeeRepo.findMany({ page: 1, pageSize: 500 })
        setEmployees('items' in res ? res.items : [])
      } catch (e) { console.error(e) }
    })()
  }, [])

  async function handleGenerate() {
    if (!selectedEmp) { setMessage('Please select an employee.'); return }
    setLoading(true)
    setMessage('')
    try {
      const emp = employees.find(e => e.id === selectedEmp)
      if (!emp) { setMessage('Employee not found.'); setLoading(false); return }

      const payRes = await payrollRepo.findMany({
        page: 1, pageSize: 500,
        sort: [{ field: 'periodStart', direction: 'asc' }],
      })
      const allPayrolls: any[] = 'items' in payRes ? payRes.items : []
      const empPayslips = allPayrolls.filter((p: any) => p.employeeId === selectedEmp)

      if (empPayslips.length === 0) {
        setMessage('No payslips found for this employee. Run payroll first.')
        setLoading(false)
        return
      }

      // Map to Payslip shape expected by TaxService
      const payslipData = empPayslips.map((p: any) => ({
        basicPay: p.basicPay ?? 0,
        overtimePay: p.overtimePay ?? 0,
        holidayPay: p.holidayPay ?? 0,
        nightDifferential: p.nightDifferential ?? 0,
        allowance: 0, otherEarnings: 0,
        thirteenthMonthPay: (emp.basicSalary ?? 0) > 0 ? Math.round(emp.basicSalary! / 12 / 2 * 100) / 100 : 0,
        grossPay: p.grossPay ?? 0,
        sssContribution: p.sssContribution ?? 0,
        philhealthContribution: p.philhealthContribution ?? 0,
        pagibigContribution: p.pagibigContribution ?? 0,
        withholdingTax: p.withholdingTax ?? 0,
        sssLoan: 0, pagibigLoan: 0, cashAdvances: 0,
        lateDeduction: 0, absenceDeduction: 0, otherDeductions: p.otherDeductions ?? 0,
        totalDeductions: p.totalDeductions ?? 0, netPay: p.netPay ?? 0,
        daysWorked: 15, hoursWorked: 120, overtimeHours: 0, lateMinutes: 0, absences: 0,
        periodStart: String(p.periodStart ?? Date.now()),
        periodEnd: String(p.periodEnd ?? Date.now() + 15 * 86400000),
        payFrequency: 'semi_monthly' as const,
      }))

      const form2316 = TaxService.computeForm2316(
        emp as any, payslipData as any, new Date().getFullYear(), DEFAULT_PAYROLL_CONFIG
      )

      setFormData(form2316)
      const text = TaxService.formatForm2316AsText(form2316 as any)
      setFormattedText(text)
      setMessage('Form 2316 generated successfully!')
    } catch (e: any) {
      setMessage('Error: ' + (e.message ?? String(e)))
    } finally { setLoading(false) }
  }

  function handlePrint() {
    if (!formattedText) return
    const w = window.open('', '_blank', 'width=800,height=600')
    if (w) {
      w.document.write(`<pre style="font-family:monospace;font-size:11px;padding:20px">${formattedText.replace(/\n/g, '<br>')}</pre>`)
      w.document.close()
      setTimeout(() => w.print(), 500)
    }
  }

  function handleDownload() {
    if (!formattedText) return
    const blob = new Blob([formattedText], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = `BIR-Form2316-${selectedEmp?.slice(0,8)}-${new Date().getFullYear()}.txt`
    a.click(); URL.revokeObjectURL(url)
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">BIR Form 2316 Generator</h2>
        <p className="mt-1 text-sm text-gray-500">
          Certificate of Compensation Payment / Tax Withheld — TRAIN Law graduated rates
        </p>
      </div>

      {/* Employee Selector */}
      <Card>
        <CardHeader title="Generate Form 2316" description="Select employee and tax year" />
        <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Employee</label>
            <select value={selectedEmp} onChange={e => setSelectedEmp(e.target.value)}
              className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
              <option value="">Select employee...</option>
              {employees.map((e: any) => (
                <option key={e.id} value={e.id}>
                  {(e as any).fullName ?? (e as any).name ?? e.id} — {(e as any).department ?? 'No Dept'}
                </option>
              ))}
            </select>
          </div>
          <Button onClick={handleGenerate} loading={loading} icon={<FileText className="h-4 w-4" />}>
            Generate Form 2316
          </Button>
        </div>
      </Card>

      {/* Message */}
      {message && (
        <div className={`mt-4 rounded-lg border p-3 text-sm ${message.includes('Error') ? 'border-red-200 bg-red-50 text-red-700' : 'border-green-200 bg-green-50 text-green-700'}`}>
          {message}
        </div>
      )}

      {/* Form 2316 Output */}
      {formData && (
        <div className="mt-6 space-y-4">
          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button onClick={handlePrint} icon={<Printer className="h-4 w-4" />} variant="secondary">Print</Button>
            <Button onClick={handleDownload} icon={<Download className="h-4 w-4" />} variant="secondary">Download TXT</Button>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              ['Taxable Income', `₱${(formData.taxableIncome ?? 0).toLocaleString()}`],
              ['Tax Due', `₱${(formData.taxDue ?? 0).toLocaleString()}`],
              ['Tax Withheld', `₱${(formData.totalTaxWithheld ?? 0).toLocaleString()}`],
              ['Status', (formData.taxOverWithheld ?? 0) > 0 ? 'Over-Withheld' : (formData.taxUnderWithheld ?? 0) > 0 ? 'Under-Withheld' : 'Balanced'],
            ].map(([label, value]) => (
              <div key={label} className="rounded-xl border border-blue-200 bg-blue-50 p-4">
                <p className="text-xs text-blue-600">{label}</p>
                <p className="mt-1 text-lg font-bold text-blue-700">{value}</p>
              </div>
            ))}
          </div>

          {/* Formatted Text */}
          <Card>
            <CardHeader title="Form 2316 Detail" description="BIR Certificate of Compensation Payment" />
            <div className="overflow-x-auto bg-gray-950 p-6 font-mono text-xs text-green-400 leading-relaxed whitespace-pre-wrap max-h-[600px] overflow-y-auto">
              {formattedText}
            </div>
          </Card>
        </div>
      )}
    </div>
  )
}
